import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useBilling } from "@/components/BillingProvider";
import api from "@/lib/api";
import { Sparkles, ArrowRight, LayoutGrid, Layers, HardDrive } from "lucide-react";

/**
 * Free-plan usage banner. Replaces the old 15-day trial countdown.
 *
 * Displayed only for users on Free plan (not admins, not paying, not unlimited).
 * Turns amber when any resource crosses 80% and rose when the user is AT the
 * limit — the CTA still points to /pricing so the user can subscribe.
 */
export default function TrialBanner() {
  const { billing } = useBilling();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["billing-usage"],
    queryFn: async () => (await api.get("/billing/usage")).data,
    enabled: !!billing && !billing.is_admin && !billing.unlimited,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!billing || billing.is_admin || billing.unlimited) return null;
  if (billing.subscription_status === "active") return null;
  if (!data?.is_free || !data?.usage) return null;

  const u = data.usage;
  const pct = (r) => (r.limit > 0 ? Math.min(100, Math.round((r.used / r.limit) * 100)) : 0);
  const atLimit = u.workspaces.used >= u.workspaces.limit
    || u.boards.used >= u.boards.limit
    || u.storage_bytes.used >= u.storage_bytes.limit;
  const nearLimit = !atLimit && (
    pct(u.workspaces) >= 80 || pct(u.boards) >= 80 || pct(u.storage_bytes) >= 80
  );

  const tone = atLimit ? "from-rose-500 to-red-500"
    : nearLimit ? "from-amber-500 to-orange-500"
    : "from-blue-600 to-sky-500";

  const kb = (b) => b < 1024 * 1024
    ? `${Math.round(b / 1024)} KB`
    : `${(b / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <button
      type="button"
      onClick={() => navigate("/pricing")}
      data-testid="free-plan-banner"
      className={`group cursor-pointer w-full px-4 py-2 text-xs sm:text-sm bg-gradient-to-r ${tone} text-white font-medium flex items-center justify-center gap-3 hover:brightness-110 transition flex-wrap`}
    >
      <Sparkles className="h-4 w-4 flex-none" />
      <span className="font-semibold">Plan gratuito</span>
      <span className="hidden sm:inline text-white/70">·</span>
      <span className="inline-flex items-center gap-1" data-testid="free-usage-workspaces">
        <Layers className="h-3 w-3" /> {u.workspaces.used}/{u.workspaces.limit}
      </span>
      <span className="inline-flex items-center gap-1" data-testid="free-usage-boards">
        <LayoutGrid className="h-3 w-3" /> {u.boards.used}/{u.boards.limit}
      </span>
      <span className="inline-flex items-center gap-1" data-testid="free-usage-storage">
        <HardDrive className="h-3 w-3" /> {kb(u.storage_bytes.used)}/{kb(u.storage_bytes.limit)}
      </span>
      <span className="hidden sm:inline text-white/80">
        {atLimit ? "· Actualiza para más" : nearLimit ? "· Estás cerca del límite" : "· Actualiza cuando lo necesites"}
      </span>
      <ArrowRight className="h-4 w-4 flex-none group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
}
